import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bug, Lightbulb, Question, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import client, { captureScreenshot } from "@/lib/api";
import { getRecentConsoleErrors, getRecentNetworkFailures } from "@/lib/betaCapture";

const TYPES = [
  { key: "bug", label: "Something's broken", icon: Bug },
  { key: "idea", label: "I have an idea", icon: Lightbulb },
  { key: "confusion", label: "This confused me", icon: Question },
];

function detectBrowser() {
  try {
    const ua = navigator.userAgent || "";
    if (/Edg\//.test(ua)) return "Edge";
    if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
    if (/Firefox\//.test(ua)) return "Firefox";
    if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return "Safari";
    return ua.slice(0, 60);
  } catch {
    return "";
  }
}

const isMac = typeof navigator !== "undefined" && /Mac/.test(navigator.platform || navigator.userAgent || "");
const SHORTCUT_HINT = isMac ? "⌘⇧F" : "Ctrl+Shift+F";

// A small gold-on-dark report button, fixed bottom-right on every screen —
// only rendered for accounts flagged as beta testers. Disappears the moment
// that flag turns off, no code change required. Also openable from anywhere
// via Ctrl/Cmd+Shift+F, so a tester never has to hunt for the button mid-flow.
export function BetaFeedbackButton() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("bug");
  const [description, setDescription] = useState("");
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!user || !user.is_beta_tester) return;
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key?.toLowerCase() === "f" && e.shiftKey && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [user]);

  if (!user || !user.is_beta_tester) return null;

  const reset = () => { setType("bug"); setDescription(""); setIncludeScreenshot(true); setSent(false); };

  const submit = async () => {
    const desc = description.trim();
    if (desc.length < 3) { toast.error("Add a little more detail first"); return; }
    setSending(true);
    try {
      let screenshot = null;
      if (includeScreenshot) screenshot = await captureScreenshot();
      await client.post("/beta/report", {
        type, description: desc, route: location.pathname,
        screenshot: screenshot || undefined,
        platform: "web", browser: detectBrowser(),
        device: `${window.innerWidth}x${window.innerHeight}`,
        console_errors: getRecentConsoleErrors(),
        network_failures: getRecentNetworkFailures(),
      });
      setSent(true);
      setTimeout(() => { setOpen(false); reset(); }, 1400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send your report — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="beta-feedback-button"
        onClick={() => setOpen(true)}
        title={`Report feedback (${SHORTCUT_HINT})`}
        className="fixed bottom-20 right-5 z-40 flex items-center gap-2 rounded-full border border-gold/50 bg-[#0B0F15] px-4 py-2.5 text-[13px] font-bold text-gold shadow-lg shadow-black/40 transition-transform hover:scale-105 hover:border-gold lg:bottom-5"
      >
        <Bug size={16} weight="bold" />
        Feedback
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent data-testid="beta-feedback-dialog" className="max-w-md border border-gold/25 bg-[#0B0F15] text-[#F6F5F1]">
          <DialogTitle className="font-display text-xl font-semibold text-[#F6F5F1]">Report something</DialogTitle>
          {!sent && (
            <p data-testid="beta-feedback-shortcut-hint" className="-mt-2 text-[11px] text-faint">
              Tip: press <span className="font-bold text-dim">{SHORTCUT_HINT}</span> anytime to open this
            </p>
          )}
          {sent ? (
            <div data-testid="beta-feedback-sent" className="py-6 text-center">
              <p className="text-[15px] font-bold text-emerald-300">Thanks — got it! 🎬</p>

              <p className="mt-1 text-[13px] text-dim">We captured your screen and a few technical details automatically.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2" data-testid="beta-feedback-type-group">
                {TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = type === t.key;
                  return (
                    <button
                      key={t.key}
                      data-testid={`beta-feedback-type-${t.key}`}
                      onClick={() => setType(t.key)}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-bold transition-colors ${active ? "border-gold bg-gold/15 text-gold" : "border-white/[0.12] text-dim hover:text-[#F6F5F1]"}`}
                    >
                      <Icon size={14} weight={active ? "fill" : "regular"} /> {t.label}
                    </button>
                  );
                })}
              </div>

              <Textarea
                data-testid="beta-feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="In one sentence, what happened?"
                className="min-h-[80px] border-white/[0.12] bg-white/[0.03] text-[#F6F5F1] placeholder:text-faint"
                maxLength={1000}
              />

              <label className="flex items-center gap-2 text-[12px] text-dim" data-testid="beta-feedback-screenshot-toggle">
                <Checkbox checked={includeScreenshot} onCheckedChange={(v) => setIncludeScreenshot(!!v)} />
                Attach a screenshot of this screen
              </label>

              <button
                data-testid="beta-feedback-submit"
                onClick={submit}
                disabled={sending || description.trim().length < 3}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[13px] font-bold text-black transition-opacity disabled:opacity-50"
              >
                <PaperPlaneTilt size={15} weight="bold" /> {sending ? "Sending…" : "Send report"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default BetaFeedbackButton;
